import { useState } from "react";
import bibtexParse from "bibtex-parse";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Search, FileText, Loader2, Sparkles } from "lucide-react";
import type { PublicationItem } from "@profolio/validators";

interface BibtexImportModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImport: (publicationData: Partial<PublicationItem>) => void;
}

export function BibtexImportModal({ open, onOpenChange, onImport }: BibtexImportModalProps) {
  const [mode, setMode] = useState<"bibtex" | "doi">("bibtex");
  const [bibtexInput, setBibtexInput] = useState("");
  const [doiInput, setDoiInput] = useState("");
  const [loading, setLoading] = useState(false);

  const handleBibtexImport = () => {
    if (!bibtexInput.trim()) {
      toast.error("Please paste a BibTeX entry.");
      return;
    }

    try {
      const parsed = bibtexParse.entries(bibtexInput);
      if (!parsed || parsed.length === 0) {
        throw new Error("No valid BibTeX entries found.");
      }

      const entry = parsed[0];
      const pubType = entry.type?.toLowerCase() === "inproceedings" || entry.type?.toLowerCase() === "conference"
        ? "conference"
        : entry.type?.toLowerCase() === "book" || entry.type?.toLowerCase() === "inbook"
        ? "book"
        : entry.type?.toLowerCase() === "phdthesis" || entry.type?.toLowerCase() === "mastersthesis"
        ? "thesis"
        : entry.type?.toLowerCase() === "techreport"
        ? "preprint"
        : "journal";

      const cleanTitle = (entry.TITLE || entry.title || "Untitled")
        .replace(/[{}]/g, "")
        .trim();

      const rawAuthors = entry.AUTHOR || entry.author || "";
      const formattedAuthors = rawAuthors
        .split(/\s+and\s+/i)
        .map((a) => a.replace(/[{}]/g, "").trim())
        .filter(Boolean)
        .join(", ");

      const venue = (entry.JOURNAL || entry.journal || entry.BOOKTITLE || entry.booktitle || entry.SCHOOL || entry.school || "")
        .replace(/[{}]/g, "")
        .trim();

      const yearParsed = parseInt(entry.YEAR || entry.year || String(new Date().getFullYear()), 10);
      const doi = (entry.DOI || entry.doi || "").trim();
      const url = (entry.URL || entry.url || (doi ? `https://doi.org/${doi}` : "")).trim();
      const abstract = (entry.ABSTRACT || entry.abstract || "").replace(/[{}]/g, "").trim();

      onImport({
        title: cleanTitle,
        authors: formattedAuthors || "Author",
        venue: venue,
        type: pubType as any,
        year: isNaN(yearParsed) ? new Date().getFullYear() : yearParsed,
        doi: doi,
        url: url,
        abstract: abstract,
        featured: false,
      });

      toast.success("Successfully parsed and imported BibTeX!");
      onOpenChange(false);
      setBibtexInput("");
    } catch {
      toast.error("Failed to parse BibTeX entry. Please check the syntax format.");
    }
  };

  const handleDoiImport = async () => {
    if (!doiInput.trim()) {
      toast.error("Please enter a DOI identifier.");
      return;
    }

    setLoading(true);
    try {
      // Clean DOI string (strip https://doi.org/ or dx.doi.org/ prefixes)
      const cleanDoi = doiInput
        .replace(/^(https?:\/\/)?(dx\.)?doi\.org\//i, "")
        .trim();

      const res = await fetch(`https://api.crossref.org/works/${encodeURIComponent(cleanDoi)}`);
      if (!res.ok) {
        throw new Error("DOI not found in CrossRef database");
      }

      const data = await res.json();
      const work = data.message;

      const authors = Array.isArray(work.author)
        ? work.author
            .map((a: any) => `${a.given || ""} ${a.family || ""}`.trim())
            .filter(Boolean)
            .join(", ")
        : "";

      const rawType = work.type || "";
      const pubType = rawType.includes("proceeding")
        ? "conference"
        : rawType.includes("book")
        ? "book"
        : rawType.includes("dissertation")
        ? "thesis"
        : rawType.includes("posted-content")
        ? "preprint"
        : "journal";

      const title = Array.isArray(work.title) && work.title.length > 0 ? work.title[0] : "Untitled Publication";
      const venue = Array.isArray(work["container-title"]) && work["container-title"].length > 0
        ? work["container-title"][0]
        : (work.publisher || "");
      const year = work.issued?.["date-parts"]?.[0]?.[0] || new Date().getFullYear();
      const doi = work.DOI || cleanDoi;
      const url = work.URL || `https://doi.org/${doi}`;
      const abstract = work.abstract
        ? work.abstract.replace(/(<([^>]+)>)/gi, "").trim()
        : "";

      onImport({
        title,
        authors: authors || "Author",
        venue,
        type: pubType as any,
        year: typeof year === "number" ? year : parseInt(String(year), 10) || new Date().getFullYear(),
        doi,
        url,
        abstract,
        featured: false,
      });

      toast.success("Successfully fetched publication metadata from CrossRef!");
      onOpenChange(false);
      setDoiInput("");
    } catch (err: any) {
      toast.error(err.message || "Failed to fetch DOI metadata from CrossRef.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Import Publication Metadata"
      description="Instantly autofill paper title, authors, venue, year, and DOI using BibTeX syntax or direct DOI lookup."
      className="max-w-xl"
    >
      <div className="space-y-4 pt-1">
        {/* Mode Selector */}
        <div className="flex gap-2 p-1 bg-muted/60 rounded-lg">
          <Button
            type="button"
            size="sm"
            variant={mode === "bibtex" ? "default" : "ghost"}
            onClick={() => setMode("bibtex")}
            className="flex-1 text-xs h-8 font-medium gap-1.5"
          >
            <FileText className="h-3.5 w-3.5" />
            <span>Paste BibTeX</span>
          </Button>
          <Button
            type="button"
            size="sm"
            variant={mode === "doi" ? "default" : "ghost"}
            onClick={() => setMode("doi")}
            className="flex-1 text-xs h-8 font-medium gap-1.5"
          >
            <Search className="h-3.5 w-3.5" />
            <span>DOI Lookup</span>
          </Button>
        </div>

        {mode === "bibtex" ? (
          <div className="space-y-3">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-foreground">BibTeX Citation Entry</label>
              <Textarea
                placeholder={`@article{smith2024quantum,
  title={Quantum Advantage in Distributed Systems},
  author={Smith, John and Doe, Jane},
  journal={Nature Computational Science},
  year={2024},
  doi={10.1038/s43588-024-00001-x}
}`}
                value={bibtexInput}
                onChange={(e) => setBibtexInput(e.target.value)}
                className="h-44 font-mono text-xs leading-relaxed resize-none"
              />
            </div>

            <Button
              type="button"
              onClick={handleBibtexImport}
              className="w-full text-xs h-9 font-semibold gap-1.5"
            >
              <Sparkles className="h-3.5 w-3.5" />
              <span>Parse & Fill Form</span>
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-foreground">Digital Object Identifier (DOI)</label>
              <Input
                placeholder="e.g. 10.1038/s41586-020-2649-2 or https://doi.org/..."
                value={doiInput}
                onChange={(e) => setDoiInput(e.target.value)}
                className="text-xs h-9 font-mono"
              />
              <p className="text-[11px] text-muted-foreground">
                Fetches indexed metadata directly from CrossRef's open DOI registry.
              </p>
            </div>

            <Button
              type="button"
              onClick={handleDoiImport}
              disabled={loading || !doiInput.trim()}
              className="w-full text-xs h-9 font-semibold gap-1.5"
            >
              {loading ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  <span>Fetching CrossRef Metadata...</span>
                </>
              ) : (
                <>
                  <Search className="h-3.5 w-3.5" />
                  <span>Fetch & Fill Form</span>
                </>
              )}
            </Button>
          </div>
        )}
      </div>
    </Dialog>
  );
}
